import React, { useContext, useEffect, useState } from "react"
import { useQuery, useLazyQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { DateTime } from "luxon"
import StaffSidebar from "../../../components/sidebars/StaffSidebar"
import PageSidebarContent from "../../../components/sidebars/PageSidebarContent"
import useLabels from "../../../hooks/useLabels"
import { UserContext } from "../../../context/userContext"
import { LoaderMedium } from "../../../components/Loaders"
import { Dropdown } from "primereact/dropdown"
import _ from "lodash"
import {
  GET_STAFF_ID_BY_MEMBERSHIP_ID,
  GET_SAVED_TEMPLATE_BY_SELECTED_STAFF,
  GET_CUSTOM_FIELD,
  GET_STAFF_NAME,
  GET_STAFF_PROFILE_BOX,
} from "../queries"

import styles from "../../../styles/Page-RecoveryPlan.module.css"

// Utility function to determine field type
const determineFieldType = (fieldName) =>
  fieldName.startsWith("custom_") ? "custom" : "system"

// Safe fetch for custom fields
const safeFetchCustomField = async (getCustomField, idField, type) => {
  if (!idField || !type) {
    console.warn(`Skipping fetch: idField=${idField}, type=${type}`)
    return { idField, label: "Missing required variables" }
  }

  try {
    const res = await getCustomField({ variables: { idField, type } })
    return {
      idField,
      label: res.data?.fieldByName?.field_label || "Unknown label",
    }
  } catch (error) {
    console.error("Error fetching custom field:", error)
    return { idField, label: "Error fetching label" }
  }
}

// Safe fetch for staff names
const safeFetchStaffName = async (fetchFunction, staff_id, organization_id) => {
  if (!staff_id || !organization_id) {
    console.warn(`Skipping fetchStaffName: staff_id=${staff_id}, organization_id=${organization_id}`)
    return { data: { getStaffName: { data: "Unknown" } } }
  }

  return await fetchFunction({ variables: { staff_id, organization_id } })
}

export default function StaffPlan() {
  const [error, setError] = useState(false)
  const [blocks, setBlocks] = useState(null)
  const [loadingLabels, labels] = useLabels(["individual_supervision_session"])
  const [customFieldDict, setCustomFieldDict] = useState({})
  const [staffNames, setStaffNames] = useState({ recent: null, first: null })
  const [selectedStatus, setSelectedStatus] = useState(null)

  const [loadingBlocks, setLoadingBlocks] = useState(false)
  const userCtx = useContext(UserContext)
  const staff_membership_id = userCtx.current_staff_membership_id
  const organization_id = userCtx.session.user.organization_id
  const idTemplate = "individual_supervision_session"
  const [userIsSupervisor, setUserIsSupervisor] = useState(false)

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(GET_STAFF_ID_BY_MEMBERSHIP_ID, {
    variables: { staff_membership_id },
    fetchPolicy: "network-only",
    skip: !staff_membership_id,
    onError: () => setError(true),
  })

  const staff_id = staffData?.staffDataByStaffMembershipID?.staff_id
  const staff_id_role_id = staffData?.staffDataByStaffMembershipID?.rol_id

  const { data: staffName, loading: loadignStaffName } = useQuery(
    GET_STAFF_NAME,
    {
      variables: { staff_id, organization_id },
      skip: !staff_id,
      fetchPolicy: "network-only",
      onError: () => setError(true),
    }
  )

  // Lazy queries
  const [getCustomField] = useLazyQuery(GET_CUSTOM_FIELD)
  const [getStaffName] = useLazyQuery(GET_STAFF_NAME, {
    fetchPolicy: "network-only",
    onError: (error) => console.error("Error fetching staff name:", error),
  })

  const statusOptions = [
    { label: "All", value: null },
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
    { label: "In the future", value: "In the future" },
    { label: "Complete", value: "Complete" },
  ]

  const filteredSupervisionGoals = blocks?.supervision_goals?.filter((goal) =>
    selectedStatus ? goal.goal_status === selectedStatus : true
  )

  const { loading: userLoading, refetch: refetchUser } = useQuery(
    GET_STAFF_PROFILE_BOX,
    {
      variables: { organization_id: null, staff_id: null }, // Initially empty, refetch will update this
      skip: true, // Skip initial fetch, we will trigger refetch with variables later
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (!data?.getStaffProfileBox || data.getStaffProfileBox.length === 0) {
          return
        }
        if (
          userCtx.session.user.staff_id ===
          data.getStaffProfileBox.staff_primary_supervisor
        )
          setUserIsSupervisor(true)
      },
      onError: () => setError(true),
    }
  )

  useEffect(() => {
    if (!staffData?.staffDataByStaffMembershipID) return

    const { organization_id, staff_id } = staffData.staffDataByStaffMembershipID

    if (organization_id && staff_id) {
      refetchUser({ organization_id, staff_id })
    }
  }, [refetchUser, staffData?.staffDataByStaffMembershipID])

  useQuery(GET_SAVED_TEMPLATE_BY_SELECTED_STAFF, {
    variables: {
      supervision_staff_membership_id: staff_membership_id,
      organization_id,
      idTemplate,
    },
    skip: !staff_id || !organization_id,
    fetchPolicy: "network-only",
    onError: (error) => console.error("Error fetching blocks: ", error),
    onCompleted: async (data) => {
      const blocksData = data?.blocksSavedByStaffAndOrg?.data

      if (!blocksData) {
        setBlocks(null)
        return
      }

      try {
        const parsedBlocks = JSON.parse(blocksData)
        const supervisionPlanBlock = parsedBlocks
          .filter(
            (item) =>
              item.block_name === "session__supervision__supervision_plan"
          )
          .reduce((mostRecent, current) => {
            const mostRecentDate = mostRecent?.date_modified?._seconds || 0
            const currentDate = current.date_modified._seconds
            return currentDate > mostRecentDate ? current : mostRecent
          }, null)
        console.log("supervisionPlanBlock", supervisionPlanBlock)
        const supervisionPlanBlockFirst = parsedBlocks
          .filter(
            (item) =>
              item.block_name === "session__supervision__supervision_plan"
          )
          .reduce((oldest, current) => {
            const oldestDate = oldest?.date_modified?._seconds || Infinity
            const currentDate = current.date_modified._seconds
            return currentDate < oldestDate ? current : oldest
          }, null)

        if (supervisionPlanBlockFirst || supervisionPlanBlock) {
          const recentStaffName = safeFetchStaffName(getStaffName, supervisionPlanBlock?.staff_id, supervisionPlanBlock?.organization_id)
          const firstStaffName = safeFetchStaffName(getStaffName, supervisionPlanBlockFirst?.staff_id, supervisionPlanBlockFirst?.organization_id)

          const [recentResult, firstResult] = await Promise.all([
            recentStaffName,
            firstStaffName,
          ])
          setStaffNames({
            recent: recentResult?.data?.getStaffName.data || "N/A",
            first: firstResult?.data?.getStaffName.data || "N/A",
          })
        }

        const customFields = {
          supervision_plan_review_frequency:
            supervisionPlanBlock?.values?.supervision_plan_review_frequency ||
            "No frequency provided",
          ..._.pickBy(supervisionPlanBlock?.values || {}, (value, key) =>
            key.startsWith("custom_")
          ),
        }

        const results = await Promise.all(
          _.keys(customFields).map(async (idField) => {
            const type = determineFieldType(idField)

            return safeFetchCustomField(getCustomField, idField, type)
          })
        )

        const mixedArray = results.map((item) => ({
          label: item.label,
          value: customFields[item.idField],
        }))

        const customFieldDictUpdate = results.reduce(
          (acc, { idField, label }) => {
            acc[idField] = label
            return acc
          },
          {}
        )

        setCustomFieldDict((prev) => ({
          ...prev,
          ...customFieldDictUpdate,
        }))

        const formattedData = {
          created_by:
            supervisionPlanBlock?.values?.supervision_plan__staff_id_selector
              ?.label || "N/A",
          date_created: supervisionPlanBlock?.date_created?._seconds
            ? DateTime.fromSeconds(
              supervisionPlanBlock.date_created._seconds
            ).toFormat("MM/dd/yyyy")
            : "N/A",
          date_modified: supervisionPlanBlock?.date_modified?._seconds
            ? DateTime.fromSeconds(
              supervisionPlanBlock.date_modified._seconds
            ).toFormat("MM/dd/yyyy")
            : "N/A",
          strengths:
            supervisionPlanBlock?.values?.supervision_plan_strengths ||
            "No strengths provided",
          five_year_goal:
            supervisionPlanBlock?.values?.supervision_plan_five_year ||
            "No 5-year goal provided",
          organizational_support:
            supervisionPlanBlock?.values?.supervision_plan_support ||
            "No support required",
          professional_goals:
            supervisionPlanBlock?.values?.professional_goals || [],
          supervision_goals:
            supervisionPlanBlock?.values?.supervision_goals || [],
          mixedArray,
        }

        setBlocks(formattedData)
      } catch (error) {
        console.error("Error parsing blocks data:", error)
        setBlocks(null)
      }
    },
  })

  const renderGroupedTasks = (tasks) => {
    if (!tasks || tasks.length === 0) {
      return <div>No tasks available</div>
    }

    const groupedTasks = _.groupBy(tasks, "task_status")
    return Object.entries(groupedTasks).map(([status, tasks], index) => (
      <div key={index} className={styles.taskGroup}>
        <h5 className={styles.taskStatusHeader}>{status}</h5>
        <ul className={styles.taskList}>
          {tasks.map((task, idx) => (
            <li key={idx} className={styles.taskItem}>
              {task.task || "No task"}
            </li>
          ))}
        </ul>
      </div>
    ))
  }
  if (staffLoading || loadignStaffName || userLoading) return <LoaderMedium />
  if (staffError) return <p>Error fetching staff data: {staffError.message}</p>
  if (!blocks) return <LoaderMedium />
  const renderHtmlContent = (content) => (
    <div dangerouslySetInnerHTML={{ __html: content || "" }} />
  )
  return (
    <div>
      <PageSidebarContent>
        <StaffSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <h1 className={styles.title}>Supervision Plan</h1>

        {userCtx.session.user.staff_role >= 0 &&
          userCtx.session.user.staff_role <= 4 &&
          staff_id_role_id !== "-1" ? (
          <Link to="/form/individual_supervision_session/1" className="btn">
            Begin {labels.individual_supervision_session || "Session"}
          </Link>
        ) : null}
        <div className="box-summary no-title">
          <ul>
            <li>
              <strong>Date Last Updated: </strong>
              {blocks?.date_modified || "N/A"}
            </li>
            <li>
              <strong>Updated by: </strong> {staffNames.recent || "N/A"}
            </li>
            <li>
              <strong>Date Plan Created: </strong>
              {blocks?.date_created || "N/A"}
            </li>
            <li>
              <strong>Created by: </strong> {staffNames.first || "N/A"}
            </li>
          </ul>
        </div>
        <div className={styles.summaryContent}>
          <div className={styles.colLeft}>
            <h4>Strengths</h4>
          </div>
          <div className={styles.colRight}>
            {renderHtmlContent(blocks?.strengths || "N/A")}
          </div>
          <div className={styles.colLeft}>
            <h4>5-year goal</h4>
          </div>
          <div className={styles.colRight}>
            {renderHtmlContent(blocks.five_year_goal)}
          </div>
          <div className={styles.colLeft}>
            <h4>Organizational Support Desired</h4>
          </div>
          <div className={styles.colRight}>
            {renderHtmlContent(
              blocks?.organizational_support?.[0]?.support_item ||
              "No support required"
            )}
          </div>
        </div>
        <h2 className={styles.subTitle}>Supervision Goals</h2>
        <div style={{ marginBottom: "1em" }}>
          <Dropdown
            value={selectedStatus}
            options={statusOptions}
            onChange={(e) => setSelectedStatus(e.value)}
            placeholder="Filter by Status"
            className="p-column-filter"
          />
        </div>
        <DataTable value={filteredSupervisionGoals} responsiveLayout="scroll">
          <Column
            field="goal"
            header="Goal"
            sortable
            body={(rowData) => (
              <div
                dangerouslySetInnerHTML={{
                  __html: rowData?.goal || "No goal available",
                }}
              />
            )}
          />
          <Column field="goal_status" sortable header="Status" />
          <Column
            field="supervision_goals_tasks"
            header="Tasks"
            body={(rowData) =>
              renderGroupedTasks(rowData?.supervision_goals_tasks || [])
            }
          />
        </DataTable>
        <h2 className={styles.subTitle}>Plan Details</h2>
        <DataTable value={blocks.mixedArray} responsiveLayout="scroll">
          <Column field="label" />
          <Column field="value" />
        </DataTable>
      </div>
    </div>
  )
}
